<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'CSAT por Canal',
  },
});

const sortedData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort(
    (a, b) => b.satisfaction_score - a.satisfaction_score
  );
});

const avgScore = computed(() => {
  if (sortedData.value.length === 0) return 0;
  const total = sortedData.value.reduce(
    (sum, i) => sum + i.satisfaction_score,
    0
  );
  return (total / sortedData.value.length).toFixed(1);
});

function getScoreColor(score) {
  if (score >= 80) return 'text-n-teal-11';
  if (score >= 60) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getBarColor(score) {
  if (score >= 80) return 'bg-n-teal-9';
  if (score >= 60) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function getEmoji(rating) {
  const emojis = { 1: '😠', 2: '😞', 3: '😐', 4: '😊', 5: '😍' };
  return emojis[rating] || '⭐';
}

function getChannelIcon(channelType) {
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-code',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-star text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Media:</span>
        <span class="text-lg font-bold" :class="getScoreColor(avgScore)">
          {{ avgScore }}%
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedData.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <span class="i-lucide-star-off text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem avaliacoes no periodo</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col gap-3 max-h-80 overflow-y-auto">
      <div
        v-for="(inbox, index) in sortedData"
        :key="inbox.inbox_id"
        class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1"
      >
        <span
          class="text-sm font-medium w-5"
          :class="index < 3 ? 'text-n-amber-11' : 'text-n-slate-10'"
        >
          {{ index + 1 }}
        </span>

        <span
          :class="getChannelIcon(inbox.channel_type)"
          class="text-lg text-n-slate-11"
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-n-slate-12 truncate">{{
              inbox.inbox_name
            }}</span>
            <span
              class="text-lg font-bold"
              :class="getScoreColor(inbox.satisfaction_score)"
            >
              {{ inbox.satisfaction_score }}%
            </span>
          </div>

          <div class="flex items-center gap-2">
            <div class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getBarColor(inbox.satisfaction_score)"
                :style="{ width: `${inbox.satisfaction_score}%` }"
              />
            </div>
          </div>

          <div class="flex items-center justify-between mt-2 text-xs">
            <span class="text-n-slate-10">{{ inbox.total_responses }} avaliacoes</span>
            <div class="flex items-center gap-1">
              <span
                v-for="rating in [5, 4, 3, 2, 1]"
                :key="rating"
                class="flex items-center gap-0.5"
                :title="`${rating} estrelas: ${inbox.distribution?.[rating] || 0}`"
              >
                <span class="text-xs">{{ getEmoji(rating) }}</span>
                <span class="text-n-slate-11">{{
                  inbox.distribution?.[rating] || 0
                }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
